/**
 * Weather icon picker — maps condition keys to icon assets.
 *
 * Icon sources:
 * - Animated SVGs from https://github.com/basmilius/weather-icons (MIT License,
 *   the production/{fill,line}/svg variants), loaded whole-directory via
 *   import.meta.glob below.
 * - Lucide components via @lucide/svelte (non-animated fallback).
 */

// Deep imports: the @lucide/svelte barrel pulls every icon into the dev
// bundle (lucide-icons/lucide#2583).
import Sun from "@lucide/svelte/icons/sun";
import Moon from "@lucide/svelte/icons/moon";
import Cloud from "@lucide/svelte/icons/cloud";
import CloudRain from "@lucide/svelte/icons/cloud-rain";
import CloudSun from "@lucide/svelte/icons/cloud-sun";
import CloudMoon from "@lucide/svelte/icons/cloud-moon";
import CloudMoonRain from "@lucide/svelte/icons/cloud-moon-rain";
import Zap from "@lucide/svelte/icons/zap";
import Snowflake from "@lucide/svelte/icons/snowflake";
import CloudSnow from "@lucide/svelte/icons/cloud-snow";
import Thermometer from "@lucide/svelte/icons/thermometer";
import CloudFog from "@lucide/svelte/icons/cloud-fog";
import CloudDrizzle from "@lucide/svelte/icons/cloud-drizzle";
import Wind from "@lucide/svelte/icons/wind";
import Haze from "@lucide/svelte/icons/haze";
import type { Component } from "svelte";

import { reportError } from "$lib/errorHandling/errorReporter";

// Every file in these folders ships in the bundle and the SW precache, so
// keep the folders curated to slugs referenced in the maps below (the icon
// integrity test enforces this in both directions).
const fillIconsByPath = import.meta.glob(
  "../../assets/weather_icons/animated/fill/*.svg",
  { eager: true, import: "default" }
) as Record<string, string>;
const lineIconsByPath = import.meta.glob(
  "../../assets/weather_icons/animated/line/*.svg",
  { eager: true, import: "default" }
) as Record<string, string>;

function bySlug(iconsByPath: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const path in iconsByPath) {
    const slug = path.replace(/^.*\/([^/]+)\.svg$/, "$1");
    out[slug] = iconsByPath[path];
  }
  return out;
}

const animatedIcons = {
  fill: bySlug(fillIconsByPath),
  line: bySlug(lineIconsByPath),
};

interface WeatherIcon {
  slug: string; // animated SVG filename without extension, same in fill/ and line/
  lucide: Component;
}

// ---------------------------------------------------------------------------
// Primary map: condition_keys emitted by the elev8-central Content API.
// Mirrors central's CODE_TO_KEY in
//   app/models/gateways/world_weather_online/weather.rb
// Many keys share one animated icon (same slug), but every central key is
// listed explicitly so the contract stays visible per WWO code.
// ---------------------------------------------------------------------------

const conditionKeyToIconMap = {
  sunny: { slug: "clear-day", lucide: Sun },
  partly_cloudy: { slug: "partly-cloudy-day", lucide: CloudSun },
  cloudy: { slug: "overcast-day", lucide: Cloud },
  overcast: { slug: "overcast-day", lucide: Cloud },
  haze: { slug: "haze-day", lucide: Haze },
  dust_haze: { slug: "dust-day", lucide: Haze },
  blowing_dust: { slug: "dust-wind", lucide: Wind },
  dust_storm: { slug: "dust-wind", lucide: Wind },
  sandstorm: { slug: "dust-wind", lucide: Wind },
  severe_sandstorm: { slug: "dust-wind", lucide: Wind },
  mist: { slug: "mist", lucide: CloudFog },
  smoke: { slug: "smoke", lucide: Haze },
  smoky_haze: { slug: "smoke", lucide: Haze },
  smog: { slug: "smoke", lucide: Haze },
  severe_smog: { slug: "extreme-smoke", lucide: Haze },
  saharan_dust: { slug: "dust-day", lucide: Haze },
  dust: { slug: "dust-day", lucide: Haze },
  patchy_rain_nearby: { slug: "partly-cloudy-day-rain", lucide: CloudRain },
  patchy_snow_nearby: { slug: "partly-cloudy-day-snow", lucide: Snowflake },
  patchy_sleet_nearby: { slug: "partly-cloudy-day-sleet", lucide: CloudSnow },
  patchy_freezing_drizzle_nearby: {
    slug: "partly-cloudy-day-sleet",
    lucide: CloudSnow,
  },
  thundery_outbreaks_in_nearby: { slug: "thunderstorms", lucide: Zap },
  blowing_snow: { slug: "wind-snow", lucide: Wind },
  blizzard: { slug: "wind-snow", lucide: Snowflake },
  fog: { slug: "fog-day", lucide: CloudFog },
  freezing_fog: { slug: "fog-day", lucide: CloudFog },
  patchy_light_drizzle: {
    slug: "partly-cloudy-day-drizzle",
    lucide: CloudDrizzle,
  },
  light_drizzle: { slug: "partly-cloudy-day-drizzle", lucide: CloudDrizzle },
  freezing_drizzle: { slug: "sleet", lucide: CloudSnow },
  heavy_freezing_drizzle: { slug: "extreme-sleet", lucide: CloudSnow },
  patchy_light_rain: { slug: "partly-cloudy-day-rain", lucide: CloudDrizzle },
  light_rain: { slug: "drizzle", lucide: CloudDrizzle },
  moderate_rain_at_times: { slug: "rain", lucide: CloudRain },
  moderate_rain: { slug: "rain", lucide: CloudRain },
  heavy_rain_at_times: { slug: "extreme-rain", lucide: CloudRain },
  heavy_rain: { slug: "extreme-rain", lucide: CloudRain },
  light_freezing_rain: { slug: "sleet", lucide: CloudSnow },
  moderate_or_heavy_freezing_rain: { slug: "extreme-sleet", lucide: CloudSnow },
  light_sleet: { slug: "sleet", lucide: CloudSnow },
  moderate_or_heavy_sleet: { slug: "extreme-sleet", lucide: CloudSnow },
  patchy_light_snow: { slug: "partly-cloudy-day-snow", lucide: Snowflake },
  light_snow: { slug: "snow", lucide: Snowflake },
  patchy_moderate_snow: { slug: "partly-cloudy-day-snow", lucide: Snowflake },
  moderate_snow: { slug: "snow", lucide: Snowflake },
  patchy_heavy_snow: { slug: "extreme-snow", lucide: Snowflake },
  heavy_snow: { slug: "extreme-snow", lucide: Snowflake },
  ice_pellets: { slug: "hail", lucide: CloudSnow },
  light_rain_shower: { slug: "drizzle", lucide: CloudDrizzle },
  moderate_or_heavy_rain_shower: { slug: "extreme-rain", lucide: CloudRain },
  torrential_rain_shower: { slug: "extreme-rain", lucide: CloudRain },
  light_sleet_showers: { slug: "sleet", lucide: CloudSnow },
  moderate_or_heavy_sleet_showers: { slug: "extreme-sleet", lucide: CloudSnow },
  light_snow_showers: { slug: "snow", lucide: Snowflake },
  moderate_or_heavy_snow_showers: { slug: "extreme-snow", lucide: Snowflake },
  light_showers_of_ice_pellets: { slug: "hail", lucide: CloudSnow },
  moderate_or_heavy_showers_of_ice_pellets: { slug: "hail", lucide: CloudSnow },
  patchy_light_rain_in_area_with_thunder: {
    slug: "thunderstorms-rain",
    lucide: Zap,
  },
  moderate_or_heavy_rain_in_area_with_thunder: {
    slug: "thunderstorms-rain",
    lucide: Zap,
  },
  patchy_light_snow_in_area_with_thunder: {
    slug: "thunderstorms",
    lucide: Zap,
  },
  moderate_or_heavy_snow_in_area_with_thunder: {
    slug: "thunderstorms",
    lucide: Zap,
  },
} satisfies Record<string, WeatherIcon>;

// String-indexable view for lookups by arbitrary input; the literal type
// above keeps key references compile-checked.
const conditionKeyLookup: Record<string, WeatherIcon | undefined> =
  conditionKeyToIconMap;

// ---------------------------------------------------------------------------
// Night overrides: conditions with visually distinct night icons. weather.rb
// appends _night to every nighttime current observation (never forecast);
// keys not listed here reuse the day icon via the strip-suffix step below.
// ---------------------------------------------------------------------------

const nightOverrides: Record<string, WeatherIcon> = {
  sunny_night: { slug: "clear-night", lucide: Moon },
  partly_cloudy_night: { slug: "partly-cloudy-night", lucide: CloudMoon },
  cloudy_night: { slug: "overcast-night", lucide: Cloud },
  overcast_night: { slug: "overcast-night", lucide: Cloud },
  fog_night: { slug: "fog-night", lucide: CloudFog },
  mist_night: { slug: "fog-night", lucide: CloudFog },
  freezing_fog_night: { slug: "fog-night", lucide: CloudFog },
  haze_night: { slug: "haze-night", lucide: Haze },
  dust_haze_night: { slug: "dust-night", lucide: Haze },
  saharan_dust_night: { slug: "dust-night", lucide: Haze },
  dust_night: { slug: "dust-night", lucide: Haze },
  patchy_rain_nearby_night: {
    slug: "partly-cloudy-night-rain",
    lucide: CloudMoonRain,
  },
  patchy_light_drizzle_night: {
    slug: "partly-cloudy-night-drizzle",
    lucide: CloudMoonRain,
  },
  light_drizzle_night: {
    slug: "partly-cloudy-night-drizzle",
    lucide: CloudMoonRain,
  },
  patchy_snow_nearby_night: {
    slug: "partly-cloudy-night-snow",
    lucide: Snowflake,
  },
  patchy_sleet_nearby_night: {
    slug: "partly-cloudy-night-sleet",
    lucide: CloudSnow,
  },
  patchy_freezing_drizzle_nearby_night: {
    slug: "partly-cloudy-night-sleet",
    lucide: CloudSnow,
  },
  patchy_light_rain_night: {
    slug: "partly-cloudy-night-rain",
    lucide: CloudMoonRain,
  },
  patchy_light_snow_night: {
    slug: "partly-cloudy-night-snow",
    lucide: Snowflake,
  },
  patchy_moderate_snow_night: {
    slug: "partly-cloudy-night-snow",
    lucide: Snowflake,
  },
};

const defaultIcon: WeatherIcon = { slug: "thermometer", lucide: Thermometer };

// ---------------------------------------------------------------------------
// Lookup logic
// ---------------------------------------------------------------------------

function getWeatherIcon(input: string): WeatherIcon {
  // 1. Night override (exact match)
  const nightMatch = nightOverrides[input];
  if (nightMatch) return nightMatch;

  // 2. Condition key (exact match)
  const keyMatch = conditionKeyLookup[input];
  if (keyMatch) return keyMatch;

  // 3. Strip _night suffix and reuse the day icon
  if (input.endsWith("_night")) {
    const dayMatch = conditionKeyLookup[input.replace(/_night$/, "")];
    if (dayMatch) return dayMatch;
  }

  // 4. Nothing matched — the map mirrors central's CODE_TO_KEY exactly, so
  //    any miss means the contract has drifted.
  reportUnknownConditionKey(input);
  return defaultIcon;
}

// One report per unknown key per page lifetime
const reportedUnknownKeys = new Set<string>();

function reportUnknownConditionKey(key: string) {
  if (reportedUnknownKeys.has(key)) return;
  reportedUnknownKeys.add(key);
  reportError(
    "[WeatherIconPicker] condition_key has no icon map entry",
    undefined,
    { condition_key: key }
  );
}

function getWeatherIconComponent(input: string): Component {
  return getWeatherIcon(input).lucide;
}

function getAnimatedWeatherIcon(
  input: string,
  style: "fill" | "line" = "fill"
): string {
  return animatedIcons[style][getWeatherIcon(input).slug];
}

export {
  getAnimatedWeatherIcon,
  getWeatherIconComponent,
  conditionKeyToIconMap,
  nightOverrides,
  defaultIcon,
  animatedIcons,
};
